import Container from '@components/common/layout'
import { UnreachableError } from '@components/common/unreachable-error'
import { CtfMarkdownText } from '@components/contentful/ctf-markdown-text'
import { cn } from '@lib/utils'
import Image from 'next/image'
import Link from 'next/link'

interface PartnerInfo {
  name: string
  image: string
  slug: string | null
}

export const partners = {
  anthropic: {
    name: 'Anthropic',
    image: 'anthropic.svg',
    slug: null,
  },
  openai: {
    name: 'Open AI',
    image: 'openai.svg',
    slug: 'openai',
  },
  aws: {
    name: 'Amazon Web Services',
    image: 'aws.svg',
    slug: 'aws',
  },
  google: {
    name: 'Google Cloud',
    image: 'google_cloud.svg',
    slug: 'google',
  },
  microsoft: {
    name: 'Microsoft',
    image: 'microsoft.svg',
    slug: 'microsoft',
  },
  databricks: {
    name: 'Databricks',
    image: 'databricks.svg',
    slug: 'databricks',
  },
} satisfies Record<string, PartnerInfo>

const PartnerRow = ({
  theme,
  partnerInfo,
}: {
  theme: 'light' | 'dark'
  partnerInfo: PartnerInfo[]
}) => {
  // md:grid-cols-${partnerInfo.length}
  return (
    <div
      className={`grid auto-cols-min grid-flow-row justify-evenly justify-items-center gap-y-4 md:grid-flow-col`}
    >
      {partnerInfo.map(({ name, image, slug }) => {
        const imageElement = (
          <Image
            alt={`Partner logo for ${name}`}
            src={`/assets/png/partners/${image}`}
            width={250}
            height={60}
            className={`mx-auto max-h-16 w-auto max-w-[200px] object-contain object-center brightness-0 ${
              theme === 'dark' ? 'invert' : ''
            }`}
            priority
          />
        )
        const linkOrSpan =
          slug == null ? (
            <span className='inline-block'>{imageElement}</span>
          ) : (
            <Link title={`Partner ${name}`} href={`/partnerships/${slug}`}>
              {imageElement}
            </Link>
          )
        return (
          <div
            key={name}
            className='flex h-16 w-min items-center justify-center'
          >
            {linkOrSpan}
          </div>
        )
      })}
    </div>
  )
}

export const OurPartners = ({
  layout,
  header,
  copy,
}: {
  layout: 'home' | 'services'
  header: string
  copy: string
}) => {
  let theme: 'light' | 'dark'
  switch (layout) {
    case 'home':
      theme = 'dark'
      break
    case 'services':
      theme = 'light'
      break
    default:
      throw new UnreachableError(layout)
  }

  const partnerInfo: PartnerInfo[] = [
    // partners.anthropic,
    partners.openai,
    partners.databricks,
    partners.aws,
    partners.google,
    partners.microsoft,
  ]

  const chunkedInfo: PartnerInfo[][] = []

  const chunkSize = 3
  for (let i = 0; i < partnerInfo.length; i += chunkSize) {
    chunkedInfo.push(partnerInfo.slice(i, i + chunkSize))
  }

  const items = (
    <div
      className={cn('flex flex-col gap-y-4 md:gap-y-14', {
        'mx-auto': layout === 'services',
        '-ml-10': layout === 'home',
      })}
    >
      {chunkedInfo.map((chunk, rowIndex) => (
        <PartnerRow key={rowIndex} theme={theme} partnerInfo={chunk} />
      ))}
    </div>
  )

  // OLD layout still used on Services
  if (layout === 'services') {
    return (
      <Container className='bg-gold'>
        <div className='mx-auto max-w-[960px] pb-32 pt-16 text-center text-black'>
          <h2
            className='text-2xl font-medium xs:text-5xl sm:text-7xl'
            dangerouslySetInnerHTML={{ __html: header }}
          />
          <CtfMarkdownText
            styling='none'
            className='my-14 text-xl xs:text-2xl sm:text-4xl'
            markdown={copy}
          ></CtfMarkdownText>
          {items}
        </div>
      </Container>
    )
  }

  // new layout on Home
  return (
    <Container className='overflow-hidden bg-black'>
      <div className='relative pb-20 pt-24'>
        <div className='relative z-10 col-span-2 flex flex-col gap-14 text-white'>
          <h2
            className='text-balance text-center text-h2 font-medium text-gold'
            dangerouslySetInnerHTML={{ __html: header }}
          />
          {items}
          <CtfMarkdownText
            styling='none'
            className='text-balance text-center text-body font-light'
            markdown={copy}
          ></CtfMarkdownText>
        </div>
      </div>
    </Container>
  )
}
